import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    fetchMatchProposals, fetchMyMatchProposals, createMatchProposal,
    acceptMatchProposal, cancelMatchProposal, fetchAvailableCourtsForSlot, checkWindowAvailability,
    fetchLeagueView, fetchMyActivity, getUserData, redirectToLoginIfInvalidToken,
    freezeLeagueParticipation, unfreezeLeagueParticipation
} from '../../api/api';
import Spinner from '../Spinner/Spinner';
import CustomModal from '../../UI/Modal/CustomModal';
import styles from './MatchPlanning.module.css';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

const formatProposalDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const day = format(date, 'dd', { locale: pl });
    const month = format(date, 'LLLL', { locale: pl });
    const dayName = format(date, 'EEEE', { locale: pl });
    return `${day} ${month} (${dayName})`;
};

const extractErrorMessage = (data) => {
    if (!data) return 'Nieznany błąd';
    if (typeof data === 'string') return data;
    if (data.message) return data.message;
    if (data.error) return data.error;
    return JSON.stringify(data);
};

const MatchPlanning = () => {
    const { tournamentId } = useParams();
    const navigate = useNavigate();

    const [leagueData, setLeagueData] = useState(null);
    const [userData, setUserData] = useState({});
    const [proposals, setProposals] = useState([]);
    const [myProposals, setMyProposals] = useState([]);
    const [myActivity, setMyActivity] = useState(null);
    const [showBreakdown, setShowBreakdown] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [proposedDate, setProposedDate] = useState('');
    const [proposedFromTime, setProposedFromTime] = useState('');
    const [proposedToTime, setProposedToTime] = useState('');

    const [modalMessage, setModalMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPositive, setIsPositive] = useState(true);
    const [isWarning, setIsWarning] = useState(false);
    const [confirmCallback, setConfirmCallback] = useState(null);

    const [cancelModal, setCancelModal] = useState(null); // { proposalId, status }
    const [cancelReason, setCancelReason] = useState('');

    const [acceptSlider, setAcceptSlider] = useState(null); // { proposalId, windowStart, windowEnd }
    const [sliderIdx, setSliderIdx] = useState(0);
    const [slotCourts, setSlotCourts] = useState([]);
    const [sliderLoading, setSliderLoading] = useState(false);
    const lastFetchedSlotRef = useRef(null); // { proposalId, slotStart } — pomija re-fetch przy otwarciu slidera
    const [statusFilter, setStatusFilter] = useState({
        ACTIVE: true, ACCEPTED: true, EXPIRED: true, CANCELED: true
    });
    const toggleStatusFilter = (status) => {
        setStatusFilter(prev => ({ ...prev, [status]: !prev[status] }));
    };

    useEffect(() => {
        redirectToLoginIfInvalidToken(navigate, `/leagues/${tournamentId}/match-planning`);
    }, [navigate, tournamentId]);

    useEffect(() => {
        const fetchUser = async () => {
            const data = await getUserData();
            setUserData(data);
        };
        fetchUser();
    }, []);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        const leagueResult = await fetchLeagueView(tournamentId, null);
        if (leagueResult.success) {
            setLeagueData(leagueResult.data);
            const round = leagueResult.data.currentRoundNumber;

            const [proposalsResult, myResult, activityResult] = await Promise.all([
                fetchMatchProposals(tournamentId, round),
                fetchMyMatchProposals(tournamentId),
                fetchMyActivity(tournamentId)
            ]);

            if (proposalsResult.success) setProposals(proposalsResult.data);
            if (myResult.success) setMyProposals(myResult.data);
            if (activityResult.success) setMyActivity(activityResult.data);
        }
        setIsLoading(false);
    }, [tournamentId]);

    useEffect(() => { loadData(); }, [loadData]);

    // Ticker 60s — wymusza re-render dla warunków zależnych od bieżącego czasu
    // (isTimeElapsed greying ofert po terminie) bez przeładowania strony.
    const [, setNowTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setNowTick(t => t + 1), 60000);
        return () => clearInterval(id);
    }, []);

    const showModal = (message, positive = true) => {
        setModalMessage(message);
        setIsPositive(positive);
        setIsWarning(false);
        setConfirmCallback(null);
        setIsModalOpen(true);
    };

    const showConfirm = (message, onConfirm, positive = true, warning = false) => {
        setModalMessage(message);
        setIsPositive(positive);
        setIsWarning(warning);
        setConfirmCallback(() => onConfirm);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setConfirmCallback(null);
        setIsWarning(false);
    };

    const handleModalConfirm = () => {
        const cb = confirmCallback;
        closeModal();
        if (cb) cb();
    };

    const handleFreeze = async () => {
        const result = await freezeLeagueParticipation(tournamentId);
        if (result.success) {
            showModal('Zamrożenie zgłoszone. Obowiązuje od następnej rundy.');
            loadData();
        } else {
            showModal(result.data?.message || 'Błąd zamrożenia', false);
        }
    };

    const handleUnfreeze = async () => {
        const result = await unfreezeLeagueParticipation(tournamentId);
        if (result.success) {
            showModal('Odmrożenie zgłoszone.');
            loadData();
        } else {
            showModal(result.data?.message || 'Błąd odmrożenia', false);
        }
    };

    const handleCreate = async (confirmed = false) => {
        if (!proposedDate || !proposedFromTime || !proposedToTime) {
            showModal('Wybierz datę i godziny okna', false);
            return;
        }
        const windowCheck = await checkWindowAvailability(tournamentId, proposedDate, proposedFromTime, proposedToTime);
        if (!windowCheck.available) {
            showModal('Brak wolnych kortów w podanym oknie czasowym.', false);
            return;
        }
        const result = await createMatchProposal(tournamentId, proposedDate, proposedFromTime, proposedToTime, confirmed);
        if (result.success) {
            if (result.data?.requiresConfirmation) {
                showConfirm(
                    'Pierwsze okno zaczyna się za mniej niż 3 godziny. Czy na pewno chcesz dodać tę ofertę?',
                    () => handleCreate(true),
                    true,
                    true
                );
                return;
            }
            showModal('Propozycja terminu dodana');
            setProposedDate('');
            setProposedFromTime('');
            setProposedToTime('');
            loadData();
        } else {
            showModal(extractErrorMessage(result.data), false);
        }
    };

    const doAccept = async (proposalId, slots = null, confirmed = false) => {
        const result = await acceptMatchProposal(tournamentId, proposalId, false, slots, confirmed);
        if (result.success) {
            if (result.data?.requiresConfirmation) {
                showConfirm(
                    'Do rozpoczęcia meczu zostało mniej niż 3 godziny. Czy na pewno chcesz przyjąć tą ofertę?',
                    () => doAccept(proposalId, slots, true),
                    true,
                    true
                );
                return;
            }
            const court = result.data?.reservedCourtInfo;
            showModal(`Mecz zaakceptowany! Zarezerwowano ${court || 'kort'}. Sprawdź email.`);
            loadData();
            return;
        }
        // COURT_TAKEN: wybrany kort jest już zajęty — zamknij slider, powiadom
        if (result.data === 'COURT_TAKEN') {
            setAcceptSlider(null);
            showModal('Wybrany kort jest już zajęty. Wybierz inny slot lub godzinę.', false);
            loadData();
            return;
        }
        showModal(extractErrorMessage(result.data), false);
        loadData();
    };

    const handleAccept = (proposal) => {
        showConfirm(
            'Czy na pewno chcesz przyjąć tę propozycję? Zostanie utworzona rezerwacja kortu.',
            async () => {
                // Oblicz sloty w oknie
                const [fh, fm] = proposal.proposedStartTime.split(':').map(Number);
                const [th, tm] = proposal.proposedEndTime.split(':').map(Number);
                const windowMins = (th * 60 + tm) - (fh * 60 + fm);
                const count = Math.floor((windowMins - 120) / 30) + 1;
                const starts = Array.from({ length: count }, (_, i) => {
                    const mins = fh * 60 + fm + i * 30;
                    return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${mins % 60 === 0 ? '00' : '30'}`;
                });

                // Sprawdź wszystkie sloty równolegle
                const results = await Promise.all(
                    starts.map(s => fetchAvailableCourtsForSlot(tournamentId, proposal.id, s))
                );
                const firstAvailableIdx = results.findIndex(r => r.success && r.data.length > 0);

                if (firstAvailableIdx === -1) {
                    // Brak kortów — backend anuluje propozycję (jak dotychczas)
                    await doAccept(proposal.id, null);
                    return;
                }

                // Preładuj korty dla pierwszego wolnego slotu, otwórz slider bez flashowania
                lastFetchedSlotRef.current = { proposalId: proposal.id, slotStart: starts[firstAvailableIdx] };
                setSliderIdx(firstAvailableIdx);
                setSlotCourts(results[firstAvailableIdx].data);
                setSliderLoading(false);
                setAcceptSlider({
                    proposalId: proposal.id,
                    proposedDate: proposal.proposedDate,
                    windowStart: proposal.proposedStartTime,
                    windowEnd: proposal.proposedEndTime,
                });
            }
        );
    };

    const handleSlotCourtPick = async (court) => {
        if (!acceptSlider || !slotStarts.length) return;
        const slotStart = slotStarts[sliderIdx];
        const slotEnd = addTwoHours(slotStart);
        const pid = acceptSlider.proposalId;
        setAcceptSlider(null);
        await doAccept(pid, [{ courtId: court.courtId, courtName: court.courtName, startTime: slotStart, endTime: slotEnd }]);
    };

    const doCancel = async (proposalId, reason) => {
        const result = await cancelMatchProposal(tournamentId, proposalId, reason || null);
        if (result.success) {
            showModal('Propozycja anulowana');
            loadData();
        } else {
            showModal(extractErrorMessage(result.data), false);
        }
    };

    const handleCancel = (proposalId, status) => {
        if (status === 'ACCEPTED') {
            setCancelReason('');
            setCancelModal({ proposalId });
        } else {
            showConfirm('Czy na pewno chcesz anulować tę propozycję?', () => doCancel(proposalId), false);
        }
    };

    const handleCancelConfirm = () => {
        if (cancelModal) {
            doCancel(cancelModal.proposalId, cancelReason);
            setCancelModal(null);
            setCancelReason('');
        }
    };

    const handleCancelDismiss = () => {
        setCancelModal(null);
        setCancelReason('');
    };

    // Opcje "do": min fromTime+2h, max 22:00 co 30 min
    const toTimeOptions = useMemo(() => {
        if (!proposedFromTime) return [];
        const [fh, fm] = proposedFromTime.split(':').map(Number);
        const minMins = fh * 60 + fm + 120;
        const maxMins = 22 * 60;
        const opts = [];
        for (let mins = minMins; mins <= maxMins; mins += 30) {
            opts.push(`${String(Math.floor(mins / 60)).padStart(2, '0')}:${mins % 60 === 0 ? '00' : '30'}`);
        }
        return opts;
    }, [proposedFromTime]);

    // Sloty suwaka: wszystkie starty 2h slotów w oknie propozycji
    const slotStarts = useMemo(() => {
        if (!acceptSlider) return [];
        const [fh, fm] = acceptSlider.windowStart.split(':').map(Number);
        const [th, tm] = acceptSlider.windowEnd.split(':').map(Number);
        const windowMins = (th * 60 + tm) - (fh * 60 + fm);
        const count = Math.floor((windowMins - 120) / 30) + 1;
        return Array.from({ length: count }, (_, i) => {
            const mins = fh * 60 + fm + i * 30;
            return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${mins % 60 === 0 ? '00' : '30'}`;
        });
    }, [acceptSlider]);

    // Pobierz dostępne korty po zatrzymaniu suwaka (debounce 250ms).
    // Pomija fetch jeśli slot jest już załadowany (ref ustawiony przy otwarciu slidera).
    useEffect(() => {
        if (!acceptSlider || !slotStarts.length) return;
        const slotStart = slotStarts[sliderIdx];
        const ref = lastFetchedSlotRef.current;
        if (ref && ref.proposalId === acceptSlider.proposalId && ref.slotStart === slotStart) return;
        setSliderLoading(true);
        const timer = setTimeout(() => {
            lastFetchedSlotRef.current = { proposalId: acceptSlider.proposalId, slotStart };
            fetchAvailableCourtsForSlot(tournamentId, acceptSlider.proposalId, slotStart)
                .then(r => setSlotCourts(r.success ? r.data : []))
                .finally(() => setSliderLoading(false));
        }, 250);
        return () => clearTimeout(timer);
    }, [sliderIdx, acceptSlider, slotStarts, tournamentId]);

    if (isLoading || !leagueData) return <Spinner />;

    const currentRound = leagueData.currentRoundNumber;
    const userRoles = new Set(userData.authorities || []);
    const isSupervisorOrAdmin = userRoles.has("ROLE_SUPERVISOR") || userRoles.has("ROLE_SUPER") || userRoles.has("ROLE_ADMIN");
    const isFrozen = leagueData.frozenInCurrentRound === true;
    const frozenForNextRound = leagueData.frozenForNextRound === true;
    const canFreeze = !frozenForNextRound && (leagueData.freezesRemaining ?? 0) > 0;
    const userGroupLabel = leagueData.userGroupLabel;

    // Filter groups: regular player sees only their group, admin/supervisor sees all
    const visibleGroups = isSupervisorOrAdmin
        ? leagueData.groupStandings
        : leagueData.groupStandings.filter(g => g.groupLabel === userGroupLabel);

    // Get current user's participantId from league data
    const myParticipantId = leagueData.userParticipantId || null;

    // Build set of played match pairs (participantId pairs where winner is set)
    const allMatches = leagueData.groupStandings.flatMap(g => g.matches);
    const hasPlayedMatch = (pid1, pid2) => {
        return allMatches.some(m =>
            m.participantWinnerId != null &&
            ((m.participant1Id === pid1 && m.participant2Id === pid2) ||
             (m.participant1Id === pid2 && m.participant2Id === pid1))
        );
    };

    // Does current user already have an ACCEPTED proposal with the given opponent in this round?
    // Scans the full proposals list. Ignores past-dated ACCEPTED proposals (stale agreements —
    // mecz nie został rozegrany w zaplanowanym terminie) — takie nie blokują nowej akceptacji.
    const hasAcceptedProposalWith = (opponentParticipantId) => {
        if (!myParticipantId || !opponentParticipantId) return false;
        const now = new Date();
        return proposals.some(p => {
            if (p.status !== 'ACCEPTED') return false;
            const isPair = (p.proposerParticipantId === myParticipantId && p.accepterParticipantId === opponentParticipantId) ||
                           (p.proposerParticipantId === opponentParticipantId && p.accepterParticipantId === myParticipantId);
            if (!isPair) return false;
            const end = new Date(p.proposedDate + 'T' + p.proposedEndTime);
            return end > now;
        });
    };

    // Check if current player has any unplayed matches left
    const hasUnplayedMatches = myParticipantId && allMatches.some(m =>
        m.participantWinnerId == null &&
        (m.participant1Id === myParticipantId || m.participant2Id === myParticipantId)
    );

    // Group proposals by group label — CANCELED znika 3 dni po anulowaniu, pozostałe 3 dni po terminie meczu.
    // Dodatkowo filtrowane przez statusFilter (przyciski Aktywna/Zaakceptowana/Anulowana/Wygasła).
    const visibleProposals = proposals
        .filter(p => {
            const now = new Date();
            if (p.status === 'CANCELED' && p.canceledAt) {
                const cutoff = new Date(p.canceledAt);
                cutoff.setDate(cutoff.getDate() + 3);
                return now <= cutoff;
            }
            const proposedDate = new Date(p.proposedDate + 'T00:00:00');
            const cutoff = new Date(proposedDate);
            cutoff.setDate(cutoff.getDate() + 3);
            return now <= cutoff;
        })
        .filter(p => statusFilter[p.status] !== false)
        .sort((a, b) => {
            const aActive = a.status === 'ACTIVE' ? 0 : 1;
            const bActive = b.status === 'ACTIVE' ? 0 : 1;
            if (aActive !== bActive) return aActive - bActive;
            const dateCompare = a.proposedDate.localeCompare(b.proposedDate);
            if (dateCompare !== 0) return dateCompare;
            return a.proposedStartTime.localeCompare(b.proposedStartTime);
        });
    const groupedProposals = {};
    for (const group of visibleGroups) {
        groupedProposals[group.groupLabel] = visibleProposals.filter(p => p.groupLabel === group.groupLabel);
    }

    // Current week boundaries (Monday–Sunday)
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const weekStart = format(new Date(today.getFullYear(), today.getMonth(), today.getDate() + mondayOffset), 'yyyy-MM-dd');
    const weekEnd = format(new Date(today.getFullYear(), today.getMonth(), today.getDate() + mondayOffset + 6), 'yyyy-MM-dd');

    // Count my proposals for status banner — current week only
    const myWeekProposals = myProposals.filter(p =>
        (p.status === 'ACTIVE' || p.status === 'ACCEPTED') &&
        p.proposedDate >= weekStart && p.proposedDate <= weekEnd
    );
    const totalProposed = myWeekProposals.length;
    const afterFiveCount = myWeekProposals.filter(p => {
        const hour = parseInt(p.proposedEndTime.split(':')[0]);
        return hour >= 19;
    }).length;

    const currentWeekBreakdown = myActivity?.weeks?.find(w => w.inProgress);
    const currentWeekProposalPoints = currentWeekBreakdown?.proposalPoints ?? 0;
    const currentWeekMatchPoints = currentWeekBreakdown?.matchPoints ?? 0;
    const currentWeekTotalPoints = currentWeekProposalPoints + currentWeekMatchPoints;
    const currentWeekHasAfter17 = currentWeekBreakdown
        ? currentWeekBreakdown.hasAfter17
        : afterFiveCount > 0;
    // Łączna suma punktów aktywności w rundzie — z backendu (zawiera tygodnie zamknięte
    // z ostateczną wartością + bieżący tydzień w jego aktualnym stanie, tygodnie
    // nierozpoczęte liczą się jako 0).
    const roundTotalActivity = myActivity?.totalActivityThisRound || 0;

    const addTwoHours = (t) => {
        const [h, m] = t.split(':').map(Number);
        return `${String(h + 2).padStart(2, '0')}:${m === 0 ? '00' : '30'}`;
    };

    // Dla ACCEPTED: pokazuje wybrany slot (slotStartTime); dla pozostałych: okno.
    const proposalTimeLabel = (p) => {
        if (p.status === 'ACCEPTED' && p.slotStartTime) {
            return `${p.slotStartTime.substring(0, 5)}–${addTwoHours(p.slotStartTime)}`;
        }
        return `${p.proposedStartTime.substring(0, 5)}–${p.proposedEndTime.substring(0, 5)}`;
    };

    // Opcje "od": 07:00 – 20:00 co 30 min
    const fromTimeOptions = [];
    for (let h = 7; h <= 20; h++) {
        fromTimeOptions.push(`${String(h).padStart(2, '0')}:00`);
        if (h < 20) fromTimeOptions.push(`${String(h).padStart(2, '0')}:30`);
    }

    const isTimeElapsed = (p) => {
        const end = new Date(p.proposedDate + 'T' + p.proposedEndTime);
        return end.getTime() < Date.now();
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'ACTIVE': return styles.statusActive;
            case 'ACCEPTED': return styles.statusAccepted;
            case 'EXPIRED': return styles.statusExpired;
            case 'CANCELED': return styles.statusCanceled;
            default: return '';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'ACTIVE': return 'Aktywna';
            case 'ACCEPTED': return 'Zaakceptowana';
            case 'EXPIRED': return 'Wygasła';
            case 'CANCELED': return 'Anulowana';
            default: return status;
        }
    };

    return (
        <div className={styles.container}>
            <button className={styles.backButton} onClick={() => navigate('/tournaments', { state: { activeTab: 'current', tournamentId: parseInt(tournamentId) } })}>
                ← Powrót
            </button>

            <h2 className={styles.title}>Planowanie meczów — Runda {currentRound}</h2>

            {userGroupLabel && (() => {
                const myGroup = leagueData.groupStandings.find(g => g.groupLabel === userGroupLabel);
                const url = myGroup?.whatsAppGroupUrl;
                if (!url) return null;
                return (
                    <div className={styles.whatsAppGroupChatRow}>
                        <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className={styles.whatsAppGroupChatButton}
                        >
                            💬 Czat grupy {userGroupLabel}
                        </a>
                    </div>
                );
            })()}

            {isFrozen ? (
                <div className={styles.frozenBanner}>
                    ❄️ Twój udział w tej rundzie jest zamrożony — nie możesz proponować ani akceptować terminów.
                    {frozenForNextRound && (
                        <button className={styles.unfreezeButton} onClick={handleUnfreeze} style={{ marginLeft: 12 }}>
                            Odmróź udział
                        </button>
                    )}
                </div>
            ) : !hasUnplayedMatches ? (
                <div className={styles.frozenBanner}>
                    Rozegrałeś już wszystkie mecze w tej rundzie lub Twój udział jest zamrożony.
                </div>
            ) : (
                <>
                    <div className={styles.statusBanner}>
                        Propozycje: <span className={currentWeekProposalPoints >= 2 ? styles.statusOk : styles.statusWarning}>
                            {currentWeekProposalPoints}/2
                        </span>
                        {' | '}
                        Mecze: <span className={styles.statusOk}>
                            +{currentWeekMatchPoints}
                        </span>
                        {' | '}
                        Razem: <span className={currentWeekTotalPoints >= 2 ? styles.statusOk : styles.statusWarning}>
                            {currentWeekTotalPoints} pkt
                        </span>
                        {' | '}
                        Po 17:00: <span className={currentWeekHasAfter17 ? styles.statusOk : styles.statusWarning}>
                            {currentWeekHasAfter17 ? '✓' : '✗'}
                        </span>
                    </div>

                    {myActivity && myActivity.weeks && myActivity.weeks.length > 0 && (
                        <section className={styles.activitySection}>
                            <button
                                type="button"
                                className={styles.breakdownToggle}
                                onClick={() => setShowBreakdown(!showBreakdown)}
                            >
                                {showBreakdown ? '▼' : '▶'} Moja aktywność w rundzie {myActivity.roundNumber}
                                <span className={styles.activityIndicator}>
                                    {' '}(razem: {roundTotalActivity})
                                </span>
                            </button>
                            {showBreakdown && (
                                <div className={styles.weekBreakdownList}>
                                    {myActivity.weeks.map(week => {
                                        const weekClass = [
                                            styles.weekItem,
                                            week.inProgress ? styles.weekInProgress : '',
                                            week.exempted ? styles.weekExempted : '',
                                            week.notStarted ? styles.weekNotStarted : '',
                                        ].filter(Boolean).join(' ');
                                        return (
                                            <div key={week.weekNumber} className={weekClass}>
                                                <div className={styles.weekHeader}>
                                                    <strong>Tydzień {week.weekNumber}</strong>
                                                    {' '}({format(new Date(week.weekStart + 'T00:00:00'), 'dd.MM')}
                                                    {' – '}
                                                    {format(new Date(week.weekEnd + 'T00:00:00'), 'dd.MM')})
                                                    {week.notStarted && <span className={styles.notStartedBadge}>planowany</span>}
                                                    {week.exempted && <span className={styles.exemptedBadge}>✓ Exempt</span>}
                                                    {week.inProgress && !week.exempted && <span className={styles.inProgressBadge}>w toku</span>}
                                                </div>
                                                {week.notStarted ? (
                                                    <div className={styles.weekDetails}>
                                                        <em>Tydzień jeszcze się nie rozpoczął.</em>
                                                    </div>
                                                ) : (
                                                    <div className={styles.weekDetails}>
                                                        Propozycje: <strong>{week.proposalPoints ?? 0}/2</strong>
                                                        {' · '}Mecze: <strong>+{week.matchPoints ?? 0}</strong>
                                                        {' · '}Razem: <strong>{week.activityPoints} pkt</strong>
                                                        {' · '}Po 17:00:{' '}
                                                        <strong className={week.hasAfter17 ? styles.statusOk : styles.statusWarning}>
                                                            {week.hasAfter17 ? '✓' : '✗'}
                                                        </strong>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </section>
                    )}

                    {/* Propose a new time */}
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Zaproponuj termin</h3>
                        <div className={styles.formStack}>
                            <div className={styles.formField}>
                                <label className={styles.formLabel}>Data</label>
                                <input
                                    className={styles.formInput}
                                    type="date"
                                    value={proposedDate}
                                    onChange={(e) => setProposedDate(e.target.value)}
                                    min={format(new Date(), 'yyyy-MM-dd')}
                                    max={leagueData.roundEndDate ? format(new Date(leagueData.roundEndDate), 'yyyy-MM-dd') : undefined}
                                />
                            </div>
                            <div className={styles.formField}>
                                <label className={styles.formLabel}>Od</label>
                                <select className={styles.formInput} value={proposedFromTime} onChange={(e) => { setProposedFromTime(e.target.value); setProposedToTime(''); }}>
                                    <option value="">— wybierz —</option>
                                    {fromTimeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className={styles.formField}>
                                <label className={styles.formLabel}>Do</label>
                                <select className={styles.formInput} value={proposedToTime} onChange={(e) => setProposedToTime(e.target.value)} disabled={!proposedFromTime}>
                                    <option value="">— wybierz —</option>
                                    {toTimeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className={styles.formButtons}>
                                <button
                                    className={styles.addButton}
                                    onClick={handleCreate}
                                    disabled={!proposedDate || !proposedFromTime || !proposedToTime}
                                >
                                    Dodaj propozycję
                                </button>
                                {frozenForNextRound ? (
                                    <button className={styles.unfreezeButton} onClick={handleUnfreeze}>
                                        Odmróź udział
                                    </button>
                                ) : canFreeze ? (
                                    <button className={styles.freezeButton} onClick={() => showConfirm(
                                        `Zamrożenie obowiązuje od następnej rundy na 1 rundę. Awanse i spadki zostaną naliczone normalnie. Pozostało zamrożeń: ${leagueData.freezesRemaining}.`,
                                        handleFreeze
                                    )}>
                                        ❄️ Zamróź ({leagueData.freezesRemaining})
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Group activity leaderboard —
                regular player: tylko własna grupa;
                admin/supervisor: wszystkie widoczne grupy (jedna sekcja per grupa) */}
            {visibleGroups.some(g => g.standings && g.standings.length > 0) && (
                <>
                    {visibleGroups
                        .filter(g => g.standings && g.standings.length > 0)
                        .filter(g => isSupervisorOrAdmin || g.groupLabel === userGroupLabel)
                        .map(group => {
                            // Sortowanie ASC po punktach aktywności — najmniej aktywni na górze
                            // (analogicznie do starej semantyki "najgorsi na górze").
                            const sorted = [...group.standings]
                                .sort((a, b) => (a.activityPointsThisRound || 0) - (b.activityPointsThisRound || 0));
                            return (
                                <section key={group.groupLabel} className={styles.groupPenaltySection}>
                                    <h4>Aktywność w grupie {group.groupLabel}</h4>
                                    <ul className={styles.groupPenaltyList}>
                                        {sorted.map(s => {
                                            const isMe = myParticipantId && s.participantId === myParticipantId;
                                            const activity = s.activityPointsThisRound || 0;
                                            const cleanedPhone = s.whatsAppContact && !isMe
                                                ? s.whatsAppContact.replace(/[^0-9+]/g, '')
                                                : null;
                                            return (
                                                <li
                                                    key={s.participantId}
                                                    className={`${styles.groupPenaltyItem} ${isMe ? styles.groupPenaltyMe : ''}`}
                                                >
                                                    <span className={styles.playerName}>
                                                        {isMe ? <strong>Ty ({s.playerName})</strong> : s.playerName}
                                                        {cleanedPhone && (
                                                            <a
                                                                href={`https://wa.me/${cleanedPhone}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                title={`Napisz do ${s.playerName} na WhatsApp`}
                                                                className={styles.waMeIcon}
                                                            >
                                                                💬
                                                            </a>
                                                        )}
                                                    </span>
                                                    <span className={styles.playerPenalty}>
                                                        <span className={styles.activityValue}>{activity}</span>
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </section>
                            );
                        })}
                </>
            )}

            {/* Active proposals per group */}
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Propozycje terminów{!isSupervisorOrAdmin && userGroupLabel ? ` — Grupa ${userGroupLabel}` : ''}</h3>
                <div className={styles.statusFilterRow}>
                    {[
                        { key: 'ACTIVE', label: 'Aktywna' },
                        { key: 'ACCEPTED', label: 'Umówiona' },
                        { key: 'CANCELED', label: 'Odwołana' },
                        { key: 'EXPIRED', label: 'Wygasła' },
                    ].map(s => (
                        <button
                            key={s.key}
                            type="button"
                            className={`${styles.statusFilterButton} ${styles[`filter${s.key}`]} ${statusFilter[s.key] ? styles.filterActive : styles.filterInactive}`}
                            onClick={() => toggleStatusFilter(s.key)}
                            aria-pressed={statusFilter[s.key]}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
                {visibleGroups.map(group => (
                    <div key={group.groupLabel} className={styles.groupBlock}>
                        {isSupervisorOrAdmin && <div className={styles.groupLabel}>Grupa {group.groupLabel}</div>}
                        {groupedProposals[group.groupLabel]?.length > 0 ? (
                            <>
                                <table className={`${styles.proposalsTable} ${styles.desktopOnly}`}>
                                    <thead>
                                        <tr>
                                            <th>Gracz</th>
                                            <th>Data</th>
                                            <th>Godzina</th>
                                            <th>Status</th>
                                            {!isFrozen && <th>Akcja</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groupedProposals[group.groupLabel].map(p => {
                                            const isOwnProposal = myParticipantId && p.proposerParticipantId === myParticipantId;
                                            const isAccepter = myParticipantId && p.accepterParticipantId === myParticipantId;
                                            const alreadyPlayed = myParticipantId && hasPlayedMatch(myParticipantId, p.proposerParticipantId);
                                            const alreadyAgreed = hasAcceptedProposalWith(p.proposerParticipantId);
                                            // Admin/supervisor widzi wszystkie grupy, ale akceptować może tylko w swojej.
                                            const isMyGroup = group.groupLabel === userGroupLabel;
                                            const canAccept = isMyGroup && p.status === 'ACTIVE' && !isOwnProposal && !alreadyPlayed && !alreadyAgreed;
                                            const canCancel = p.status === 'ACCEPTED' && (isOwnProposal || isAccepter);
                                            const elapsed = isTimeElapsed(p);
                                            return (
                                                <tr key={p.id} className={elapsed ? styles.elapsed : undefined}>
                                                    <td>{p.proposerName}</td>
                                                    <td>{formatProposalDate(p.proposedDate)}</td>
                                                    <td>{proposalTimeLabel(p)}</td>
                                                    <td>
                                                        <span className={`${styles.statusTag} ${getStatusClass(p.status)}`}>
                                                            {getStatusLabel(p.status)}
                                                        </span>
                                                        {p.status === 'ACCEPTED' && p.accepterName && (
                                                            <span className={styles.accepterInfo}> — {p.accepterName}</span>
                                                        )}
                                                    </td>
                                                    {!isFrozen && (
                                                        <td>
                                                            {canAccept && (
                                                                <button className={styles.acceptButton} onClick={() => handleAccept(p)}>
                                                                    Przyjmij
                                                                </button>
                                                            )}
                                                            {canCancel && (
                                                                <button className={styles.cancelButton} onClick={() => handleCancel(p.id, p.status)}>
                                                                    Anuluj
                                                                </button>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                <div className={styles.mobileOnly}>
                                    {groupedProposals[group.groupLabel].map(p => {
                                        const isOwnProposal = myParticipantId && p.proposerParticipantId === myParticipantId;
                                        const isAccepter = myParticipantId && p.accepterParticipantId === myParticipantId;
                                        const alreadyPlayed = myParticipantId && hasPlayedMatch(myParticipantId, p.proposerParticipantId);
                                        const alreadyAgreed = hasAcceptedProposalWith(p.proposerParticipantId);
                                        const isMyGroup = group.groupLabel === userGroupLabel;
                                        const canAccept = isMyGroup && p.status === 'ACTIVE' && !isOwnProposal && !alreadyPlayed && !alreadyAgreed;
                                        const canCancel = p.status === 'ACCEPTED' && (isOwnProposal || isAccepter);
                                        const elapsed = isTimeElapsed(p);
                                        return (
                                            <div key={p.id} className={`${styles.proposalCard}${elapsed ? ' ' + styles.elapsed : ''}`}>
                                                <div className={styles.proposalCardHeader}>
                                                    <span className={styles.proposalCardPlayer}>{p.proposerName}</span>
                                                    <span className={`${styles.statusTag} ${getStatusClass(p.status)}`}>
                                                        {getStatusLabel(p.status)}
                                                    </span>
                                                </div>
                                                <div className={styles.proposalCardDetails}>
                                                    {formatProposalDate(p.proposedDate)} · {proposalTimeLabel(p)}
                                                </div>
                                                {p.status === 'ACCEPTED' && p.accepterName && (
                                                    <div className={styles.accepterInfo}>Przyjął: {p.accepterName}</div>
                                                )}
                                                {!isFrozen && canAccept && (
                                                    <button className={styles.acceptButton} onClick={() => handleAccept(p)}>
                                                        Przyjmij
                                                    </button>
                                                )}
                                                {!isFrozen && canCancel && (
                                                    <button className={styles.cancelButton} onClick={() => handleCancel(p.id, p.status)}>
                                                        Anuluj
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <div className={styles.noProposals}>Brak propozycji w ostatnich dniach</div>
                        )}
                    </div>
                ))}
            </div>

            {/* My proposals */}
            {!isFrozen && (
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Moje propozycje</h3>
                    {myProposals.length > 0 ? (
                        <>
                            <table className={`${styles.proposalsTable} ${styles.desktopOnly}`}>
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Godzina</th>
                                        <th>Status</th>
                                        <th>Akcja</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...myProposals].sort((a, b) => {
                                        const dateCompare = a.proposedDate.localeCompare(b.proposedDate);
                                        if (dateCompare !== 0) return dateCompare;
                                        return a.proposedStartTime.localeCompare(b.proposedStartTime);
                                    }).map(p => (
                                        <tr key={p.id} className={isTimeElapsed(p) ? styles.elapsed : undefined}>
                                            <td>{formatProposalDate(p.proposedDate)}</td>
                                            <td>{proposalTimeLabel(p)}</td>
                                            <td>
                                                <span className={`${styles.statusTag} ${getStatusClass(p.status)}`}>
                                                    {getStatusLabel(p.status)}
                                                </span>
                                            </td>
                                            <td>
                                                {(p.status === 'ACTIVE' || p.status === 'ACCEPTED') && (
                                                    <button className={styles.cancelButton} onClick={() => handleCancel(p.id, p.status)}>
                                                        Anuluj
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className={styles.mobileOnly}>
                                {[...myProposals].sort((a, b) => {
                                    const dateCompare = a.proposedDate.localeCompare(b.proposedDate);
                                    if (dateCompare !== 0) return dateCompare;
                                    return a.proposedStartTime.localeCompare(b.proposedStartTime);
                                }).map(p => (
                                    <div key={p.id} className={`${styles.proposalCard}${isTimeElapsed(p) ? ' ' + styles.elapsed : ''}`}>
                                        <div className={styles.proposalCardHeader}>
                                            <span className={styles.proposalCardDetails}>
                                                {formatProposalDate(p.proposedDate)} · {proposalTimeLabel(p)}
                                            </span>
                                            <span className={`${styles.statusTag} ${getStatusClass(p.status)}`}>
                                                {getStatusLabel(p.status)}
                                            </span>
                                        </div>
                                        {(p.status === 'ACTIVE' || p.status === 'ACCEPTED') && (
                                            <button className={styles.cancelButton} onClick={() => handleCancel(p.id, p.status)}>
                                                Anuluj
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className={styles.noProposals}>Nie masz jeszcze żadnych propozycji</div>
                    )}
                </div>
            )}

            <CustomModal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                message={modalMessage}
                isPositiveMessage={isPositive}
                isWarningMessage={isWarning}
                confirmLabel={confirmCallback ? 'Tak' : undefined}
                onConfirm={confirmCallback ? handleModalConfirm : undefined}
                cancelLabel={confirmCallback ? 'Anuluj' : 'Zamknij'}
                cancelButtonColor={confirmCallback ? undefined : 'green'}
                showConfirmButton={!!confirmCallback}
            />

            {cancelModal && (
                <div className={styles.cancelOverlay} onClick={handleCancelDismiss}>
                    <div className={styles.cancelModalContent} onClick={e => e.stopPropagation()}>
                        <h3>Anulowanie zaakceptowanego meczu</h3>
                        <p className={styles.cancelModalInfo}>Rezerwacja kortu zostanie usunięta, a rywal otrzyma powiadomienie email.</p>
                        <label className={styles.cancelModalLabel}>Powód anulowania:</label>
                        <textarea
                            className={styles.cancelModalTextarea}
                            value={cancelReason}
                            onChange={e => setCancelReason(e.target.value)}
                            rows={3}
                            placeholder="Podaj powód anulowania..."
                        />
                        <div className={styles.cancelModalButtons}>
                            <button className={styles.cancelButton} onClick={handleCancelDismiss}>Nie</button>
                            <button className={styles.addButton} onClick={handleCancelConfirm}>Wykonaj</button>
                        </div>
                    </div>
                </div>
            )}

            {acceptSlider && (
                <div className={styles.cancelOverlay} onClick={() => setAcceptSlider(null)}>
                    <div className={styles.sliderModalContent} onClick={e => e.stopPropagation()}>
                        <p className={styles.sliderDate}>{formatProposalDate(acceptSlider.proposedDate)}</p>
                        <h3>Wybierz godzinę i kort</h3>
                        <p className={styles.sliderLabel}>
                            {slotStarts[sliderIdx]} – {addTwoHours(slotStarts[sliderIdx])}
                        </p>
                        {slotStarts.length > 1 && (
                            <input
                                type="range"
                                min={0}
                                max={slotStarts.length - 1}
                                step={1}
                                value={sliderIdx}
                                onChange={e => setSliderIdx(Number(e.target.value))}
                                className={styles.sliderInput}
                            />
                        )}
                        {slotCourts.length === 0 && !sliderLoading ? (
                            <p className={styles.noCourts}>Brak wolnych kortów o tej godzinie</p>
                        ) : slotCourts.length > 0 ? (
                            <div className={`${styles.courtPickButtons} ${sliderLoading ? styles.courtPickButtonsLoading : ''}`}>
                                {slotCourts.map(c => (
                                    <button key={c.courtId} className={styles.courtPickButton}
                                            onClick={() => handleSlotCourtPick(c)}>
                                        {c.courtName}
                                    </button>
                                ))}
                            </div>
                        ) : null}
                        <div className={styles.cancelModalButtons}>
                            <button className={styles.cancelButton} onClick={() => setAcceptSlider(null)}>Anuluj</button>
                        </div>
                    </div>
                </div>
            )}
            <div className={styles.infoLinkWrapper}>
                <a href="/liga-info" className={styles.infoLink}>Szczegółowy opis ligi</a>
            </div>
        </div>
    );
};

export default MatchPlanning;
